// The widget registry — the canonical list of widgets the canvas can place.
// Adding a widget = one entry here (title + default size + which filters it
// `consumes` + which sections it belongs to + the component). This IS the
// filter→widget logic as data (PLAN §7B): a widget only reacts to the filters
// it names in `consumes`, and receives the current section's `WidgetScope`.
//
// FE2b shipped PlayerBio (canary, both sections). FE3 adds the real Leagues
// widgets (League Table + Match Results); the bio stays for now and moves to the
// Players section proper in FE5.

import type { ComponentType } from "react";
import type { LayoutSection } from "@catbento/shared";
import type { WidgetScope } from "./scope.js";
import LeagueTableWidget from "./LeagueTableWidget.js";
import MatchResultsWidget from "./MatchResultsWidget.js";
import BioWidget from "./BioWidget.js";
import NotesWidget from "./NotesWidget.js";
import StatsWidget from "./StatsWidget.js";
import ShotMapWidget from "./ShotMapWidget.js";
import PassMapWidget from "./PassMapWidget.js";
import HopsWidget from "./HopsWidget.js";
import AppearancesWidget from "./AppearancesWidget.js";
import FullStatsWidget from "./FullStatsWidget.js";
import RadarWidget from "./RadarWidget.js";
import PassingSummaryWidget from "./PassingSummaryWidget.js";
import PassingRadarWidget from "./PassingRadarWidget.js";
import PassingPercentilesWidget from "./PassingPercentilesWidget.js";
import PassingStyleWidget from "./PassingStyleWidget.js";
import PassingDifficultyWidget from "./PassingDifficultyWidget.js";
import PassingTendenciesWidget from "./PassingTendenciesWidget.js";
import StatsTableWidget from "./StatsTableWidget.js";

/** A canvas widget's definition. `consumes` lists the filter keys it reads
 *  (empty = self-contained, ignores the filter bar). The component receives the
 *  active section's `WidgetScope` plus its own session-only `instanceId` (used by
 *  widgets that keep per-instance state, e.g. the Stats Table's query drawer —
 *  FE4). Widgets that need neither prop just ignore them. */
export interface WidgetProps {
  scope: WidgetScope;
  instanceId: string;
}
export interface WidgetDef {
  key: string;
  title: string;
  defaultSize: { w: number; h: number };
  consumes: readonly string[];
  sections: readonly LayoutSection[];
  component: ComponentType<WidgetProps>;
}

export const WIDGETS: Record<string, WidgetDef> = {
  "league-table": {
    key: "league-table",
    title: "League Table",
    defaultSize: { w: 6, h: 9 },
    consumes: ["league", "season"],
    sections: ["leagues"],
    component: LeagueTableWidget,
  },
  "match-results": {
    key: "match-results",
    title: "Match Results",
    defaultSize: { w: 6, h: 9 },
    consumes: ["league", "season"],
    sections: ["leagues"],
    component: MatchResultsWidget,
  },
  "player-stats-table": {
    key: "player-stats-table",
    title: "Stats Table",
    // Wider/taller by default — it's a multi-column, multi-row table.
    defaultSize: { w: 8, h: 10 },
    consumes: ["league", "season"],
    sections: ["leagues"],
    component: StatsTableWidget,
  },
  "player-bio": {
    key: "player-bio",
    title: "Player Bio",
    defaultSize: { w: 6, h: 8 },
    consumes: ["player"],
    sections: ["players"],
    component: BioWidget,
  },
  "player-hops": {
    key: "player-hops",
    title: "HOPS Rating",
    // Compact: a big number, a percentile bar, and a short distribution curve.
    defaultSize: { w: 5, h: 6 },
    // Rating from /player; the curve's gender from the scoped competition.
    consumes: ["player", "competition"],
    sections: ["players"],
    component: HopsWidget,
  },
  "player-appearances": {
    key: "player-appearances",
    title: "Appearances",
    // Tall: one row per match (a full season can be ~38 rows, then it scrolls).
    defaultSize: { w: 6, h: 9 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: AppearancesWidget,
  },
  "player-notes": {
    key: "player-notes",
    title: "Notes",
    defaultSize: { w: 5, h: 8 },
    consumes: ["player"],
    sections: ["players"],
    component: NotesWidget,
  },
  "player-stats": {
    key: "player-stats",
    title: "Player Stats",
    defaultSize: { w: 6, h: 5 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: StatsWidget,
  },
  "player-full-stats": {
    key: "player-full-stats",
    title: "Full Stats",
    // Large: every metric grouped into 5 categories → it scrolls.
    defaultSize: { w: 8, h: 10 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: FullStatsWidget,
  },
  "player-radar": {
    key: "player-radar",
    title: "Radar",
    // Square-ish: the radar plus its axis labels + template dropdown.
    defaultSize: { w: 6, h: 9 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: RadarWidget,
  },
  "player-passing": {
    key: "player-passing",
    title: "Passing Summary",
    // Headline passing numbers (positional basis + peer avg). Short — a stat grid.
    defaultSize: { w: 6, h: 5 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: PassingSummaryWidget,
  },
  "player-passing-radar": {
    key: "player-passing-radar",
    title: "Passing Radar",
    // Square-ish like the Radar widget: the passing profile + peer-avg overlay + legend.
    defaultSize: { w: 6, h: 9 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: PassingRadarWidget,
  },
  "player-passing-percentiles": {
    key: "player-passing-percentiles",
    title: "Passing Percentiles",
    // 8 stacked rows of 0–100 bars + a header/footnote.
    defaultSize: { w: 6, h: 7 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: PassingPercentilesWidget,
  },
  "player-passing-style": {
    key: "player-passing-style",
    title: "Passing Style",
    // Two stacked share bars (attempt + direction mix) with legends.
    defaultSize: { w: 6, h: 6 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: PassingStyleWidget,
  },
  "player-passing-difficulty": {
    key: "player-passing-difficulty",
    title: "Passing Difficulty Bands",
    // Diverging PSAX-by-band chart + a 7-column exact-numbers table.
    defaultSize: { w: 6, h: 7 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: PassingDifficultyWidget,
  },
  "player-passing-tendencies": {
    key: "player-passing-tendencies",
    title: "Passing Tendencies",
    // Final-third direction mix + a grid of style descriptors.
    defaultSize: { w: 6, h: 6 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: PassingTendenciesWidget,
  },
  "player-shot-map": {
    key: "player-shot-map",
    title: "Shot Map",
    // Landscape-ish to fit the attacking-third pitch (80×60) plus header + legend.
    defaultSize: { w: 7, h: 9 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: ShotMapWidget,
  },
  "player-pass-map": {
    key: "player-pass-map",
    title: "Pass Map",
    // Wide to fit the full landscape pitch (120×80) plus header + legend.
    defaultSize: { w: 8, h: 9 },
    consumes: ["player", "competition", "season"],
    sections: ["players"],
    component: PassMapWidget,
  },
};

/** Lookup by key; undefined for an unknown/removed widget (the canvas then
 *  renders a small "unknown widget" tile instead of crashing). */
export function getWidget(key: string): WidgetDef | undefined {
  return WIDGETS[key];
}

/** The widgets offered in a section's palette. */
export function widgetsForSection(section: LayoutSection): WidgetDef[] {
  return Object.values(WIDGETS).filter((w) => w.sections.includes(section));
}
